<template>
  <div class="align-self-center" style="display: inline-flex;">
    <!-- Кнопка для открытия диалога -->
    <v-icon
      v-if="stripeAvailable"
      class="ml-2 mt-3 primary--text"
      @click="openDialog"
      v-tooltip="{
        content: $t('stripe_payment.pay_with_stripe'),
        placement: 'bottom-center',
        classes: ['info'],
        targetClasses: ['it-has-a-tooltip'],
        delay: {
          show: 800,
          hide: 500,
        },
      }"
    >
      mdi-alpha-s-circle-outline
    </v-icon>

    <!-- Диалог создания платежа -->
    <v-dialog v-model="showDialog" :retain-focus="false" persistent max-width="520px">
      <v-card>
        <v-card-title>
          {{ $t('stripe_payment.dialog_title') }}
        </v-card-title>
        <v-card-text>
          <p class="text--secondary mb-3">
            {{ $t('stripe_payment.dialog_hint') }}
          </p>
          <v-text-field
            v-model="paymentAmount"
            :label="$t('stripe_payment.amount')"
            type="number"
            min="0"
            step="0.01"
            outlined
            :disabled="isLoading || paymentStatus === 'pending'"
          ></v-text-field>
          <v-textarea
            v-model="paymentDescription"
            :label="$t('stripe_payment.description_field')"
            rows="1"
            auto-grow
            outlined
            :disabled="isLoading || paymentStatus === 'pending'"
          ></v-textarea>
          <v-text-field
            v-if="paymentUrl"
            :value="paymentUrl"
            :label="$t('stripe_payment.payment_link_label')"
            readonly
            outlined
            dense
            class="mt-2"
            append-icon="mdi-content-copy"
            @click:append="copyToClipboard(paymentUrl)"
          ></v-text-field>

          <!-- QR Code -->
          <div v-if="paymentUrl && qrCode" class="text-center mt-4 mb-2">
            <v-card flat>
              <v-card-text>
                <img :src="qrCode" alt="QR Code" style="max-width: 250px; width: 100%;" />
              </v-card-text>
            </v-card>
          </div>

          <!-- Ошибка -->
          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-2">
            {{ errorMessage }}
          </v-alert>

          <!-- Статус -->
          <v-alert
            v-if="paymentStatus"
            :color="getStatusColor(paymentStatus)"
            dense
            outlined
            class="mt-2"
          >
            {{ translateStatus(paymentStatus) }}
          </v-alert>

          <!-- Кнопки для отправки ссылки -->
          <div v-if="paymentUrl" class="mt-3 d-flex flex-wrap gap-2 justify-center">
            <v-btn
              small
              outlined
              color="primary"
              @click="sendToEmail"
              :loading="isSendingEmail"
              :disabled="isSendingEmail"
            >
              <v-icon small left>mdi-email-outline</v-icon>
              {{ $t('stripe_payment.send_email') }}
            </v-btn>

            <v-btn
              small
              outlined
              color="success"
              @click="sendToWhatsApp"
              :loading="isSendingWhatsApp"
              :disabled="isSendingWhatsApp"
            >
              <v-icon small left>mdi-whatsapp</v-icon>
              {{ $t('stripe_payment.send_whatsapp') }}
            </v-btn>

            <v-btn small text color="primary" @click="openExternalLink(paymentUrl)">
              <v-icon small left>mdi-open-in-new</v-icon>
              {{ $t('stripe_payment.open_link') }}
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeDialog">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="createPayment"
            :loading="isLoading"
            :disabled="isLoading || paymentStatus === 'pending' || !!paymentUrl"
          >
            {{ $t('stripe_payment.create_payment') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог возврата -->
    <v-dialog v-model="showRefundDialog" :retain-focus="false" persistent max-width="500px">
      <v-card>
        <v-card-title>{{ $t('stripe_payment.refund_title') }}</v-card-title>
        <v-card-text>
          <p class="text--secondary mb-3">
            {{ $t('stripe_payment.refund_hint') }}
          </p>
          <div v-if="refundPayment" class="mb-3">
            <strong>{{ $t('stripe_payment.order_id') }}:</strong> {{ refundPayment.order_id }}<br>
            <strong>{{ $t('stripe_payment.payment_amount') }}:</strong>
            {{ formatAmount(refundPayment.amount) }} {{ refundPayment.currency || currency }}<br>
            <span v-if="refundPayment.refunded_amount > 0">
              <strong>{{ $t('stripe_payment.already_refunded') }}:</strong>
              {{ formatAmount(refundPayment.refunded_amount) }} {{ refundPayment.currency || currency }}<br>
              <strong>{{ $t('stripe_payment.available_for_refund') }}:</strong>
              {{ formatAmount(refundPayment.amount - refundPayment.refunded_amount) }} {{ refundPayment.currency || currency }}
            </span>
          </div>
          <v-text-field
            v-model="refundAmount"
            :label="$t('stripe_payment.refund_amount')"
            type="number"
            min="0"
            step="0.01"
            :max="refundPayment ? (refundPayment.amount - (refundPayment.refunded_amount || 0)) : 0"
            outlined
            :disabled="isRefunding"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeRefundDialog" :disabled="isRefunding">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            @click="confirmRefund"
            :loading="isRefunding"
            :disabled="isRefunding || !checkAdminManager"
          >
            {{ $t('stripe_payment.confirm_refund') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { copyToClipboard as copyTextToClipboard } from '@/utils/clipboard';

export default {
  name: 'StripePaymentDialog',
  props: {
    client: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'RUB'
    },
    checkAdminManager: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDialog: false,
      paymentAmount: null,
      paymentDescription: '',
      paymentUrl: null,
      qrCode: null,
      isLoading: false,
      paymentStatus: null,
      paymentId: null,
      orderId: null,
      errorMessage: null,
      isSendingEmail: false,
      isSendingWhatsApp: false,
      showRefundDialog: false,
      refundPayment: null,
      refundAmount: null,
      isRefunding: false,
    };
  },
  computed: {
    stripeAvailable() {
      return (
        this.company &&
        this.company.stripe_enabled &&
        this.company.stripe_publishable_key &&
        this.company.stripe_secret_key
      );
    },
  },
  methods: {
    openDialog() {
      if (!this.stripeAvailable) {
        this.$notify(
          this.$t('stripe_payment.not_configured_title'),
          this.$t('stripe_payment.not_configured_text'),
          'warning',
          4000
        );
        return;
      }
      this.resetDialog();
      this.showDialog = true;
    },
    closeDialog() {
      this.stopPolling();
      this.showDialog = false;
      this.resetDialog();
    },
    resetDialog() {
      this.paymentAmount = null;
      this.paymentDescription = '';
      this.paymentUrl = null;
      this.qrCode = null;
      this.paymentStatus = null;
      this.paymentId = null;
      this.orderId = null;
      this.errorMessage = null;
    },
    createPayment() {
      if (!this.client || !this.client.id) {
        this.$notify(this.$t('stripe_payment.no_client_error'), '', 'warning', 4000);
        return;
      }
      if (!this.stripeAvailable) {
        this.$notify(
          this.$t('stripe_payment.not_configured_title'),
          this.$t('stripe_payment.not_configured_text'),
          'warning',
          4000
        );
        return;
      }
      const amountValue = this.paymentAmount ? this.paymentAmount.toString().replace(',', '.') : '';
      const amount = parseFloat(amountValue);
      if (!amount || amount <= 0) {
        this.$notify(this.$t('stripe_payment.amount_error'), '', 'warning', 4000);
        return;
      }

      this.isLoading = true;
      this.errorMessage = null;
      this.paymentStatus = 'pending';

      const payload = {
        client_id: this.client.id,
        amount: amount.toFixed(2),
      };

      if (this.paymentDescription && this.paymentDescription.length > 0) {
        payload.description = this.paymentDescription;
      }

      this.axios
        .post('/api/v1/integrations/stripe_payments/orders', payload, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          if (!response.data || !response.data.checkout_url) {
            this.paymentStatus = null;
            this.errorMessage = this.$t('stripe_payment.no_payment_url');
            return;
          }
          this.paymentUrl = response.data.checkout_url;
          this.paymentId = response.data.payment_id;
          this.orderId = response.data.order_id;

          this.generateQrCode(response.data.checkout_url);
          this.$notify(
            this.$t('stripe_payment.link_ready_title'),
            this.$t('stripe_payment.link_ready_text'),
            'info',
            5000
          );
          this.startPolling();
        })
        .catch((error) => {
          this.paymentStatus = null;
          this.errorMessage =
            (error.response && error.response?.data && error.response?.data.error) ||
            error.message ||
            this.$t('stripe_payment.status_error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    generateQrCode(url) {
      if (!url) {
        this.qrCode = null;
        return;
      }
      QRCode.toDataURL(
        url,
        {
          width: 250,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        },
        (err, dataUrl) => {
          if (err) {
            console.error('QR Code generation error:', err);
            this.qrCode = null;
          } else {
            this.qrCode = dataUrl;
          }
        }
      );
    },
    startPolling() {
      // Обновления статуса приходят через родительский ClientCard (forward → handlePaymentUpdate),
      // который подписан на StripePaymentChannel. Диалог НЕ держит собственную подписку.
      // (FT-066: под нативным @rails/actioncable коллизия растворена структурно — у карточки
      // свой held-объект подписки; но диалог всё равно не подписывается, форвард из карточки
      // остаётся единственным каналом доставки. Ср. StripePaymentDialogPublic.)
      this.paymentStatus = 'pending';
    },
    stopPolling() {
      // no-op: диалог не держит собственную WS-подписку (см. startPolling).
      // Метод сохранён — вызывается из closeDialog/cancelled/beforeDestroy.
    },
    handlePaymentUpdate(data) {
      const payment = data.stripe_payment || data.payment || {};

      // Проверяем что это наш платеж
      if (this.paymentId && payment.id !== this.paymentId) {
        return;
      }
      if (this.orderId && payment.order_id !== this.orderId) {
        return;
      }

      // Обновляем статус
      this.paymentStatus = payment.status;
      if (payment.payment_url) {
        this.paymentUrl = payment.payment_url;
      }

      // Если платеж завершен успешно
      if (payment.status === 'success') {
        this.$notify(this.$t('stripe_payment.success_title'), this.$t('stripe_payment.success_text'), 'success', 5000);
        this.closeDialog();
      } else if (payment.status === 'failed') {
        this.errorMessage = this.$t('stripe_payment.failed_message');
      } else if (payment.status === 'cancelled') {
        this.stopPolling();
        this.errorMessage = this.$t('stripe_payment.cancelled_message');
      }

      // Обновляем список платежей
      this.$emit('payments-updated');
    },
    sendToEmail() {
      if (!this.client.email) {
        this.$notify(this.$t('stripe_payment.no_email'), '', 'warning', 4000);
        return;
      }
      if (!this.paymentId) {
        this.$notify(this.$t('stripe_payment.no_payment_id'), '', 'warning', 4000);
        return;
      }

      this.isSendingEmail = true;
      this.axios
        .post(
          `/api/v1/integrations/stripe_payments/payments/${this.paymentId}/send_email`,
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then(() => {
          this.$notify(
            this.$t('stripe_payment.email_sent_title'),
            this.$t('stripe_payment.email_sent_text', { email: this.client.email }),
            'success',
            4000
          );
        })
        .catch((error) => {
          this.$notify(
            this.$t('stripe_payment.email_failed_title'),
            (error.response && error.response?.data && error.response?.data.error) || error.message,
            'error',
            4000
          );
        })
        .finally(() => {
          this.isSendingEmail = false;
        });
    },
    sendToWhatsApp() {
      if (!this.client.phone) {
        this.$notify(this.$t('stripe_payment.no_phone'), '', 'warning', 4000);
        return;
      }
      if (!this.paymentId) {
        this.$notify(this.$t('stripe_payment.no_payment_id'), '', 'warning', 4000);
        return;
      }

      this.isSendingWhatsApp = true;
      this.axios
        .post(
          `/api/v1/integrations/stripe_payments/payments/${this.paymentId}/send_whatsapp`,
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          if (response.data && response.data.whatsapp_url) {
            window.open(response.data.whatsapp_url, '_blank');
          }
          this.$notify(
            this.$t('stripe_payment.whatsapp_sent_title'),
            this.$t('stripe_payment.whatsapp_sent_text', { phone: this.client.phone }),
            'success',
            4000
          );
        })
        .catch((error) => {
          this.$notify(
            this.$t('stripe_payment.whatsapp_failed_title'),
            (error.response && error.response?.data && error.response?.data.error) || error.message,
            'error',
            4000
          );
        })
        .finally(() => {
          this.isSendingWhatsApp = false;
        });
    },
    openRefundDialog(payment) {
      this.refundPayment = payment;
      const availableForRefund = payment.amount - (payment.refunded_amount || 0);
      this.refundAmount = availableForRefund;
      this.showRefundDialog = true;
    },
    closeRefundDialog() {
      this.showRefundDialog = false;
      this.refundPayment = null;
      this.refundAmount = null;
    },
    confirmRefund() {
      if (!this.refundPayment || !this.refundAmount) {
        return;
      }

      const refundAmount = parseFloat(this.refundAmount);
      const availableForRefund = this.refundPayment.amount - (this.refundPayment.refunded_amount || 0);

      if (refundAmount <= 0 || refundAmount > availableForRefund) {
        this.$notify(this.$t('stripe_payment.invalid_refund_amount'), '', 'warning', 4000);
        return;
      }

      this.isRefunding = true;
      this.axios
        .post(
          `/api/v1/integrations/stripe_payments/payments/${this.refundPayment.id}/refund`,
          {
            refund_amount: refundAmount.toFixed(2),
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          this.$notify(
            this.$t('stripe_payment.refund_initiated_title'),
            response.data.message || this.$t('stripe_payment.refund_initiated_text'),
            'success',
            5000
          );
          this.closeRefundDialog();
          this.$emit('payments-updated');
        })
        .catch((error) => {
          this.$notify(
            this.$t('stripe_payment.refund_failed_title'),
            (error.response && error.response?.data && error.response?.data.error) ||
              error.message ||
              this.$t('stripe_payment.refund_failed_text'),
            'error',
            4000
          );
        })
        .finally(() => {
          this.isRefunding = false;
        });
    },
    async copyToClipboard(text) {
      try {
        await copyTextToClipboard(text);
        this.$notify(this.$t('common.copied'), '', 'success', 2000);
      } catch (error) {
        this.$notify(this.$t('custom.copy_error'), '', 'error');
      }
    },
    openExternalLink(url) {
      window.open(url, '_blank');
    },
    translateStatus(status) {
      const translations = {
        created: this.$t('stripe_payment.status_created'),
        pending: this.$t('stripe_payment.status_pending'),
        success: this.$t('stripe_payment.status_success'),
        failed: this.$t('stripe_payment.status_failed'),
        cancelled: this.$t('stripe_payment.status_cancelled'),
        refunded: this.$t('stripe_payment.status_refunded'),
        partially_refunded: this.$t('stripe_payment.status_partially_refunded'),
      };
      return translations[status] || status;
    },
    getStatusColor(status) {
      const colors = {
        created: 'info',
        pending: 'warning',
        success: 'success',
        failed: 'error',
        cancelled: 'grey',
        refunded: 'orange',
        partially_refunded: 'orange',
      };
      return colors[status] || 'info';
    },
    formatAmount(value) {
      return parseFloat(value).toFixed(2);
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>

